import React, { useCallback, useEffect, useState } from 'react';
import {
    AssistantAdminChat,
    AssistantAdminStats,
    AssistantChatFilter,
    getAssistantAdminStats,
    listAssistantAdminChats,
} from '../../api/assistant';
import { ApiError } from '../../api/request';
import MiniMarkdown from '../../components/assistant/MiniMarkdown';
import Pagination from '../../components/Pagination';
import { formatTime } from '../../utils/time';
import { BarList, StatCard } from '../assets/parts';

// 助手反馈 (管理员, 2026-10-14): 看大家问了什么、哪些回答没帮上 → 据此补平台使用指引
// (material-library internal/app/assistant/guide.md). 评价本身不会让 AI 自动变好, 改指引才会.

const PAGE_SIZE = 20;
const DAY_OPTIONS = [7, 30, 90];
const FILTERS: { key: AssistantChatFilter; label: string }[] = [
    { key: 'down', label: '👎 没帮上' },
    { key: 'up', label: '👍 有帮助' },
    { key: 'none', label: '未评价' },
    { key: 'error', label: '生成失败' },
    { key: 'all', label: '全部' },
];

const errText = (e: unknown) =>
    e instanceof ApiError ? e.message.split('|').pop() || e.message : String(e);

const FeedbackTag = ({ c }: { c: AssistantAdminChat }) => {
    if (c.status === 'error')
        return <span className="tag bg-rose-50 text-rose-600">生成失败</span>;
    if (c.status === 'running')
        return <span className="tag bg-violet-50 text-violet-600">生成中</span>;
    if (c.feedback === 'down')
        return (
            <span className="tag bg-amber-100 text-amber-800">👎 没帮上</span>
        );
    if (c.feedback === 'up')
        return (
            <span className="tag bg-emerald-50 text-emerald-700">
                👍 有帮助
            </span>
        );
    return <span className="tag bg-slate-100 text-slate-500">未评价</span>;
};

const AssistantFeedback = () => {
    const [days, setDays] = useState(7);
    const [stats, setStats] = useState<AssistantAdminStats | null>(null);
    const [filter, setFilter] = useState<AssistantChatFilter>('down');
    const [keyword, setKeyword] = useState('');
    const [kwInput, setKwInput] = useState('');
    const [page, setPage] = useState(1);
    const [list, setList] = useState<AssistantAdminChat[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState<number>();

    useEffect(() => {
        getAssistantAdminStats(days)
            .then(setStats)
            .catch((e) => setError(errText(e)));
    }, [days]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await listAssistantAdminChats({
                days,
                filter,
                keyword,
                page,
                pageSize: PAGE_SIZE,
            });
            setList(r.list || []);
            setTotal(r.totalCount);
        } catch (e) {
            setError(errText(e));
        } finally {
            setLoading(false);
        }
    }, [days, filter, keyword, page]);

    useEffect(() => {
        load();
    }, [load]);

    const rated = stats ? stats.up + stats.down : 0;
    const satisfaction =
        stats && rated > 0 ? `${Math.round((stats.up / rated) * 100)}%` : '—';

    return (
        <div className="space-y-5">
            <div className="flex items-end gap-3 flex-wrap">
                <div>
                    <div className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 via-violet-500 to-fuchsia-500 text-white text-[11px]">
                            ✦
                        </span>
                        助手反馈
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        AI 平台助手的提问与评价。评价不会让 AI 自动变好 ——
                        看「没帮上」和高频问题, 把缺的内容补进平台使用指引,
                        下次大家问到就答得好了。
                    </div>
                </div>
                <div className="ml-auto flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
                    {DAY_OPTIONS.map((d) => (
                        <button
                            key={d}
                            type="button"
                            className={`h-7 px-3 rounded-md text-xs ${
                                days === d
                                    ? 'bg-violet-600 text-white'
                                    : 'text-slate-600 hover:bg-slate-50'
                            }`}
                            onClick={() => {
                                setDays(d);
                                setPage(1);
                            }}
                        >
                            近 {d} 天
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                <StatCard label="提问数" value={stats?.questions ?? '—'} />
                <StatCard label="提问人数" value={stats?.askers ?? '—'} />
                <StatCard
                    label="👍 有帮助"
                    value={stats?.up ?? '—'}
                    accent="text-emerald-600"
                />
                <StatCard
                    label="👎 没帮上"
                    value={stats?.down ?? '—'}
                    accent="text-amber-600"
                />
                <StatCard
                    label="满意率"
                    value={satisfaction}
                    sub={rated ? `${rated} 条有评价` : '还没有评价'}
                />
                <StatCard
                    label="生成失败"
                    value={stats?.errors ?? '—'}
                    accent={stats?.errors ? 'text-rose-600' : undefined}
                />
                <StatCard
                    label="费用 (估算)"
                    value={stats ? `$${stats.costUsd.toFixed(2)}` : '—'}
                    sub={
                        stats?.avgElapsedMs
                            ? `平均 ${(stats.avgElapsedMs / 1000).toFixed(1)}s / 问`
                            : undefined
                    }
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <BarList
                    title="提问最多的页面"
                    hint="括号内为没帮上的条数"
                    labelWidth="w-44"
                    rows={(stats?.routes || []).map((r) => ({
                        label: `${r.routeName || '未知页面'}${r.down ? ` (${r.down})` : ''}`,
                        value: r.count,
                        color: r.down
                            ? 'rgba(245,158,11,.75)'
                            : 'rgba(139,92,246,.7)',
                    }))}
                />
                <BarList
                    title="高频问题"
                    hint="原文相同且问过 2 次以上"
                    labelWidth="w-56"
                    rows={(stats?.topQuestions || []).map((q) => ({
                        label: q.question,
                        value: q.count,
                        color: q.down
                            ? 'rgba(245,158,11,.75)'
                            : 'rgba(139,92,246,.7)',
                        onClick: () => {
                            setKwInput(q.question);
                            setKeyword(q.question);
                            setFilter('all');
                            setPage(1);
                        },
                    }))}
                />
            </div>

            <div className="card">
                <div className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-slate-100">
                    {FILTERS.map((f) => (
                        <button
                            key={f.key}
                            type="button"
                            className={`h-7 px-3 rounded-full text-xs border ${
                                filter === f.key
                                    ? 'border-violet-300 bg-violet-50 text-violet-700 font-medium'
                                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                            onClick={() => {
                                setFilter(f.key);
                                setPage(1);
                            }}
                        >
                            {f.label}
                        </button>
                    ))}
                    <input
                        className="ml-auto h-8 w-64 rounded-md border border-slate-200 px-3 text-xs outline-none focus:border-violet-300"
                        placeholder="搜问题 / 没帮上的原因 / 提问人 (回车)"
                        value={kwInput}
                        onChange={(e) => setKwInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                setKeyword(kwInput.trim());
                                setPage(1);
                            }
                        }}
                    />
                </div>

                {error && (
                    <div className="px-4 py-3 text-xs text-rose-600">
                        {error}
                    </div>
                )}
                {!error && !loading && list.length === 0 && (
                    <div className="px-4 py-10 text-center text-xs text-slate-400">
                        这段时间没有符合条件的问答
                    </div>
                )}
                <div className="divide-y divide-slate-100">
                    {list.map((c) => {
                        const open = expanded === c.id;
                        return (
                            <div
                                key={c.id}
                                className={open ? 'bg-violet-50/30' : ''}
                            >
                                <button
                                    type="button"
                                    className="w-full text-left px-4 py-3 hover:bg-slate-50/80"
                                    onClick={() =>
                                        setExpanded(open ? undefined : c.id)
                                    }
                                >
                                    <div className="flex items-center gap-2 text-[11px] text-slate-400">
                                        <span className="w-3 text-slate-400">
                                            {open ? '▾' : '▸'}
                                        </span>
                                        <span className="tabular-nums">
                                            {formatTime(c.createdAt)}
                                        </span>
                                        <span>·</span>
                                        <span className="truncate max-w-[220px]">
                                            {c.operatorEmail}
                                        </span>
                                        {c.routeName && (
                                            <>
                                                <span>·</span>
                                                <span className="truncate max-w-[220px]">
                                                    {c.routeName}
                                                </span>
                                            </>
                                        )}
                                        <span className="ml-auto">
                                            <FeedbackTag c={c} />
                                        </span>
                                    </div>
                                    <div className="mt-1 pl-5 text-[13px] text-slate-800">
                                        {c.question}
                                    </div>
                                    {c.feedbackNote && (
                                        <div className="mt-1 pl-5 text-xs text-amber-700">
                                            原因: {c.feedbackNote}
                                        </div>
                                    )}
                                </button>
                                {open && (
                                    <div className="px-4 pb-4 pl-9">
                                        <div className="rounded-lg border border-violet-200 border-l-4 border-l-violet-500 bg-white">
                                            <div className="flex items-center gap-2 px-3 py-2 border-b border-violet-100 text-xs">
                                                <span className="font-semibold text-violet-700">
                                                    ✦ 当时的回答
                                                </span>
                                                {c.elapsedMs > 0 && (
                                                    <span className="text-slate-400">
                                                        ·{' '}
                                                        {(
                                                            c.elapsedMs / 1000
                                                        ).toFixed(1)}
                                                        s
                                                    </span>
                                                )}
                                                {c.costUsd > 0 && (
                                                    <span className="text-slate-400">
                                                        · $
                                                        {c.costUsd.toFixed(4)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="px-3 py-2.5 text-[13px] text-slate-700">
                                                {c.status === 'error' ? (
                                                    <span className="text-rose-600 text-xs">
                                                        {c.error || '生成失败'}
                                                    </span>
                                                ) : (
                                                    <MiniMarkdown
                                                        text={
                                                            c.answer ||
                                                            '（无内容）'
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {total > PAGE_SIZE && (
                    <div className="px-4 py-3 border-t border-slate-100">
                        <Pagination
                            page={page}
                            totalPages={Math.ceil(total / PAGE_SIZE)}
                            total={total}
                            onChange={setPage}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssistantFeedback;
